import type { CreateFormData } from "@/components/create/types";
import type { AgentPhase } from "@/types/createAgent";
import {
  evaluateAgentActionReadiness,
  hasConfirmedBackDesignDirection,
  type AgentActionReadiness,
} from "@/lib/agent/actionReadinessGuards";
import {
  DESIGNER_RENDER_ACTION_PRESENTATION,
  type DesignerRenderActionId,
} from "@/lib/agent/designerActionPresentation";
import type { DesignSpec } from "@/types/designSpec";
import type { DesignerRenderProjection } from "./actionProjection";
import type {
  ApplyDesignChangeStep,
  DesignRenderSide,
} from "./applyDesignChanges";

/**
 * The workspace render control, as pure projections.
 *
 * The designer workspace's render button (desktop footer, mobile design panel,
 * and the copy mobile floats above the chat) used to be derived inline in
 * React components. The terminal harness and chat lab could not import that,
 * so they re-derived it and drifted: they missed the mobile chat floater
 * entirely, which made a correct "Select Generate preview" reply look like a
 * dead end. Components and harnesses now call these same functions.
 */

/** The combined apply a chosen option triggers (DesignerWorkspaceProvider). */
export interface DesignerRenderApplyPlan {
  creditCost: number;
  pendingSides: DesignRenderSide[];
  unavailableReason: string | null;
}

/** A render capability without its browser-only `execute` closure. */
export interface DesignerRenderCapabilityDescriptor {
  key: string;
  actionId: DesignerRenderActionId;
  /** The exact current choice commits locally, then applies this combined plan. */
  applyPlan?: DesignerRenderApplyPlan;
}

export interface DesignerRenderActionStateDescriptor {
  capability: DesignerRenderCapabilityDescriptor | null;
  unavailableActionId: DesignerRenderActionId | null;
  unavailableReason: string | null;
  blockedReason?: string | null;
  awaitingChoice?: boolean;
}

/**
 * Which chat choice, if any, owns the next render. A choice whose render would
 * only repeat the saved preview (every rendered side has a saved spec and
 * nothing is pending) grants no capability.
 */
export function projectDesignerRenderActionState(input: {
  projection: DesignerRenderProjection;
  /** The pending apply when a front exists and a choice owns the render. */
  plannedApply:
    | (DesignerRenderApplyPlan & { hasPendingChanges: boolean })
    | null;
  /** Every rendered side has a saved design spec. */
  savedRenderIsCurrent: boolean;
  /** An explicit Back choice still needs its first image even if Front is current. */
  hasBackImage?: boolean;
}): DesignerRenderActionStateDescriptor {
  const { projection, savedRenderIsCurrent } = input;
  const action = projection.action;
  const plannedApply = input.plannedApply?.hasPendingChanges
    ? input.plannedApply
    : null;
  const unrenderedBackChoice =
    projection.actionId === "generate_dress_back" && !input.hasBackImage;
  return {
    capability:
      action &&
      projection.actionId &&
      !(savedRenderIsCurrent && !plannedApply && !unrenderedBackChoice)
        ? {
            key: action.key,
            actionId: projection.actionId,
            ...(plannedApply
              ? {
                  applyPlan: {
                    creditCost: plannedApply.creditCost,
                    pendingSides: plannedApply.pendingSides,
                    unavailableReason: plannedApply.unavailableReason,
                  },
                }
              : {}),
          }
        : null,
    unavailableActionId: projection.action ? null : projection.actionId,
    unavailableReason: projection.unavailableReason,
    ...(projection.blockedReason
      ? { blockedReason: projection.blockedReason }
      : {}),
    ...(projection.awaitingChoice ? { awaitingChoice: true } : {}),
  };
}

/** The workspace-owned Details apply (useApplyDesignChanges). */
export interface DesignerWorkspaceApplySummary {
  draft?: DesignSpec | null;
  steps: Pick<ApplyDesignChangeStep, "side" | "mode">[];
  hasPendingChanges: boolean;
  busy: boolean;
  canApply: boolean;
  creditCost: number;
  unavailableReason: string | null;
}

export interface DesignerRenderControlInput {
  phase: AgentPhase;
  activeView: DesignRenderSide;
  frontImageUrl: string | null;
  /** Back generation needs the saved Front source, not only its display URL. */
  frontImagePath: string | null;
  backImageUrl: string | null;
  formState: Partial<CreateFormData>;
  hasBackDesignDirection?: boolean;
  renderActionState?: DesignerRenderActionStateDescriptor | null;
  apply: DesignerWorkspaceApplySummary;
  detailsEditing: boolean;
  creditsLoading: boolean;
}

export interface DesignerRenderControl {
  hasDesign: boolean;
  needsFrontPreview: boolean;
  renderSide: DesignRenderSide;
  renderId: DesignerRenderActionId;
  readiness: AgentActionReadiness;
  hasPendingChanges: boolean;
  /** Pending Details edits with no chat choice to carry them. */
  needsCommittedApply: boolean;
  /** Why the control cannot run now; null when it can. */
  actionReason: string | null;
  /** The control has something to do (it may still be disabled). */
  hasAction: boolean;
  /** Whether the current exact chat choice executes this render. */
  usesCapability: boolean;
  canRender: boolean;
  creditCost: number;
  /** Label of the compact mobile control (design panel and chat floater). */
  mobileLabel: string;
}

export function projectDesignerRenderControl(
  input: DesignerRenderControlInput
): DesignerRenderControl {
  const { phase, frontImageUrl, backImageUrl, formState, apply } = input;
  const hasBackDesignDirection =
    input.hasBackDesignDirection ??
    hasConfirmedBackDesignDirection(apply.draft);
  const hasDesign = Boolean(frontImageUrl || backImageUrl);
  const needsFrontPreview = !frontImageUrl;
  const delegatedId =
    input.renderActionState?.capability?.actionId ??
    input.renderActionState?.unavailableActionId;
  // The selected tab is only a viewing preference. A back action from a prior
  // turn cannot make the first preview target Back before Front exists.
  const prematureBackAction = Boolean(
    needsFrontPreview &&
    delegatedId &&
    DESIGNER_RENDER_ACTION_PRESENTATION[delegatedId].side === "back"
  );
  const capability = prematureBackAction
    ? null
    : (input.renderActionState?.capability ?? null);
  const hasPendingChanges =
    Boolean(capability?.applyPlan) || apply.hasPendingChanges;
  const needsCommittedApply = hasPendingChanges && !capability;
  const committedStep = needsCommittedApply ? apply.steps[0] : undefined;
  // A confirmed Back direction is ready to render even if the latest chat
  // turn only acknowledged a typed revision instead of staging a render
  // widget. The saved brief, not a stale widget from an earlier turn, owns
  // this fallback. Any committed Apply step still takes precedence.
  const renderCommittedBack = Boolean(
    frontImageUrl &&
    !backImageUrl &&
    hasBackDesignDirection &&
    !capability &&
    !committedStep
  );
  const applicableDelegatedId = prematureBackAction ? null : delegatedId;
  // The pending apply executes its first committed step before any unselected
  // chat option. Its metadata must describe that same operation.
  const renderSide: DesignRenderSide = needsFrontPreview
    ? "front"
    : (committedStep?.side ??
      (renderCommittedBack
        ? "back"
        : applicableDelegatedId
          ? DESIGNER_RENDER_ACTION_PRESENTATION[applicableDelegatedId].side
          : phase === "back_design" && !backImageUrl
            ? "back"
            : input.activeView));
  const renderId: DesignerRenderActionId = needsFrontPreview
    ? "generate_dress_front"
    : committedStep
      ? `${committedStep.mode}_dress_${committedStep.side}`
      : renderCommittedBack
        ? "generate_dress_back"
        : (applicableDelegatedId ??
          (renderSide === "front"
            ? frontImageUrl
              ? "modify_dress_front"
              : "generate_dress_front"
            : backImageUrl
              ? "modify_dress_back"
              : "generate_dress_back"));
  const readiness = evaluateAgentActionReadiness({
    actionId: renderId,
    phase,
    formState,
    hasFrontImage: Boolean(frontImageUrl),
    hasBackImage: Boolean(backImageUrl),
    frontApproved: formState.frontApproved === true,
    hasBackDesignDirection,
  });
  const prematureBackGate =
    prematureBackAction && delegatedId
      ? evaluateAgentActionReadiness({
          actionId: delegatedId,
          phase,
          formState,
          hasFrontImage: false,
          hasBackImage: Boolean(backImageUrl),
          frontApproved: formState.frontApproved === true,
          hasBackDesignDirection,
        })
      : null;
  const workspaceBlock = input.renderActionState?.blockedReason;
  // A stale Back readiness failure is not a workspace-wide block. Keep real
  // restoration/auth/busy blocks while evaluating this first Front preview.
  const applicableBlock =
    prematureBackGate &&
    !prematureBackGate.allowed &&
    workspaceBlock === prematureBackGate.message
      ? null
      : workspaceBlock;
  // A ready committed brief can be rendered without pretending an unselected
  // chat option was chosen. The option remains available as a separate choice.
  const renderCommittedFront =
    needsFrontPreview &&
    !capability &&
    readiness.allowed &&
    input.renderActionState?.awaitingChoice === true;
  // A selected Back card may be legacy data without an actual back design.
  // The capability keeps its CTA visible, but cannot bypass Back readiness.
  const requiresBackReadiness =
    renderId === "generate_dress_back" && !backImageUrl;
  const existingActionReason = input.detailsEditing
    ? "Save or cancel your edits first."
    : apply.busy
      ? "Wait for the current update to finish."
      : input.creditsLoading
        ? "Checking available credits."
        : needsCommittedApply
          ? apply.unavailableReason
          : (capability?.applyPlan?.unavailableReason ??
            (prematureBackAction
              ? applicableBlock
              : renderCommittedFront ||
                  (renderCommittedBack &&
                    input.renderActionState?.awaitingChoice === true)
                ? workspaceBlock
                : input.renderActionState?.unavailableReason) ??
            ((!capability || requiresBackReadiness) && !readiness.allowed
              ? readiness.message
              : null));
  const missingFrontSourceForBack =
    renderId === "generate_dress_back" &&
    !backImageUrl &&
    !input.frontImagePath?.trim();
  const actionReason =
    existingActionReason ??
    (missingFrontSourceForBack
      ? "The saved front image is unavailable. Regenerate the front preview before creating the back."
      : null);
  const hasAction =
    needsCommittedApply ||
    Boolean(capability) ||
    needsFrontPreview ||
    renderCommittedBack;
  const canRender =
    hasAction && !actionReason && (!needsCommittedApply || apply.canApply);
  const creditCost =
    capability?.applyPlan?.creditCost ??
    (needsCommittedApply ? apply.creditCost : 1);
  const mobileLabel =
    renderId === "generate_dress_back" && !backImageUrl
      ? "Generate back"
      : hasPendingChanges
        ? "Apply changes"
        : needsFrontPreview
          ? "Generate preview"
          : hasDesign
            ? "Apply changes"
            : "Generate preview";
  return {
    hasDesign,
    needsFrontPreview,
    renderSide,
    renderId,
    readiness,
    hasPendingChanges,
    needsCommittedApply,
    actionReason: actionReason ?? null,
    hasAction,
    usesCapability: Boolean(capability),
    canRender,
    creditCost,
    mobileLabel,
  };
}

/**
 * Whether CreateAgentChat mounts the host the mobile floater portals into:
 * only in an active conversation (not the entry screen) while nothing is
 * streaming, loading, or being uploaded.
 */
export function canMountMobilePreviewActionsHost(input: {
  sharedWorkspace: boolean;
  /** conversationMode === "chat": the customer has started the conversation. */
  conversationStarted: boolean;
  requestInFlight: boolean;
  composerUploadPending: boolean;
  optimisticEntryMessage: boolean;
}): boolean {
  return (
    input.sharedWorkspace &&
    input.conversationStarted &&
    !input.requestInFlight &&
    !input.composerUploadPending &&
    !input.optimisticEntryMessage
  );
}

/**
 * Whether mobile floats the render control above the chat. It shows whenever
 * the design panel is hidden and nothing is running, as long as there is
 * something to render: no preview yet (disabled until the brief is ready), a
 * chat choice that owns a render, or pending changes.
 */
export function isMobileChatPreviewActionVisible(input: {
  control: Pick<DesignerRenderControl, "hasAction" | "hasPendingChanges">;
  /** Desktop layout renders the control inline instead. */
  showInlineDetails: boolean;
  mobileDesignPanelVisible: boolean;
  hostMounted: boolean;
  /** Apply, render, continue, quote, or try-on work in progress. */
  workInProgress: boolean;
}): boolean {
  return (
    !input.showInlineDetails &&
    !input.mobileDesignPanelVisible &&
    input.hostMounted &&
    !input.workInProgress &&
    (input.control.hasAction || input.control.hasPendingChanges)
  );
}

/**
 * Why no chat choice may take the render right now (DesignerWorkspaceProvider
 * passes it to projectDesignerRenderAction as `blockedReason`).
 */
export function designerRenderBlockedReason(input: {
  /** Layout-level block, e.g. a saved design still restoring. */
  layoutBlockedReason?: string | null;
  quoteSubmitting: boolean;
  authLoading: boolean;
  /** A render, agent turn, preset action, or preset load is in flight. */
  workInFlight: boolean;
}): string | null {
  return (
    input.layoutBlockedReason ??
    (input.quoteSubmitting
      ? "Your quote is being prepared."
      : input.authLoading
        ? "Preparing your account."
        : input.workInFlight
          ? "Wait for the current update to finish."
          : null)
  );
}

function creditLabel(cost: number): string {
  return `${cost} ${cost === 1 ? "credit" : "credits"}`;
}

/** The desktop workspace footer button's text. */
export function designerRenderFooterLabel(
  control: Pick<
    DesignerRenderControl,
    "needsFrontPreview" | "canRender" | "creditCost" | "renderId"
  >,
  imageActionPending: boolean
): string {
  const base = imageActionPending
    ? control.renderId === "generate_dress_back"
      ? "Generating back preview…"
      : "Applying changes…"
    : control.needsFrontPreview
      ? "Generate preview"
      : control.renderId === "generate_dress_back"
        ? "Generate back preview"
        : "Apply changes";
  return control.canRender
    ? `${base} · ${creditLabel(control.creditCost)}`
    : control.needsFrontPreview
      ? `${base} · 1 credit`
      : base;
}

/** The compact mobile button's text (design panel and chat floater). */
export function designerRenderCompactLabel(
  control: Pick<DesignerRenderControl, "mobileLabel" | "creditCost">
): string {
  return `${control.mobileLabel} · ${creditLabel(control.creditCost)}`;
}

/**
 * Whether the mobile design panel shows the render button. Inline Details
 * editing keeps the button in place; the control disables it until saved.
 */
export function isMobileDesignPanelRenderVisible(input: {
  control: Pick<DesignerRenderControl, "hasAction" | "hasPendingChanges">;
  quoteSubmitting: boolean;
  tryOnGenerating: boolean;
}): boolean {
  return (
    !input.quoteSubmitting &&
    !input.tryOnGenerating &&
    (input.control.hasAction || input.control.hasPendingChanges)
  );
}
